import { Link2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import { OnlineBookingSharePanel } from '@/components/booking/OnlineBookingSharePanel'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export function OnlineBookingShareModal({ open, onClose }) {
  useEffect(() => {
    if (!open) {
      return undefined
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    function handleEscape(event) {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleEscape)
    }
  }, [open, onClose])

  if (!open) {
    return null
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[200] overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-label="Share online booking"
    >
      <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
        <button
          type="button"
          aria-label="Close dialog backdrop"
          className="fixed inset-0 bg-foreground/45 backdrop-blur-sm"
          onClick={onClose}
        />
        <div className="relative z-10 flex w-full max-w-3xl flex-col overflow-hidden rounded-3xl border border-border/60 bg-card shadow-2xl">
          <OnlineBookingSharePanel variant="modal" onClose={onClose} />
        </div>
      </div>
    </div>,
    document.body,
  )
}

export function OnlineBookingShareDialog({ className, onOpen }) {
  const [open, setOpen] = useState(false)

  function handleOpen() {
    setOpen(true)
    onOpen?.()
  }

  function handleClose() {
    setOpen(false)
  }

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={cn('h-9 shrink-0 rounded-xl px-2.5', className)}
        onClick={handleOpen}
        aria-label="Share online booking"
        title="Share booking"
      >
        <Link2 className="h-4 w-4" />
      </Button>

      <OnlineBookingShareModal open={open} onClose={handleClose} />
    </>
  )
}
